import * as TabsPrimitive from '@radix-ui/react-tabs'
import { Link, Typography } from '@cannect/new-components/atoms'
import { CtaPill } from '@cannect/new-components/molecules'
import SearchAutocomplete from '@cannect/new-components/organisms/Header/SearchAutocomplete'
import { CarePathModal } from '@cannect/new-components/organisms/CarePathModal'
import { HERO_SLUG_BY_LABEL, isNewHomeScreening } from '@cannect/new-components/organisms/HeroSection/heroSlugs'
import { usePublicScreeningModules } from '@cannect/services/resources/screening/hooks'
import { LuArrowRightCircle } from 'react-icons/lu'
import { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { PAGE_PATH, programHomeSearch } from '@cannect/pages/WeightLossProgram/constants'
import { useProductClassId } from '@cannect/services/resources/classes'
import { buildCategoryCards, DISCOVERY_TABS, HEALTH_CONDITION_CARDS } from './constants'

/**
 * Abas centralizadas em forma de pill (node 4439:13628). A revisão de 2026-07-31 abandonou o
 * formato de pasta: a ativa ganha fundo e borda, a inativa fica sem preenchimento, e as duas
 * passaram a carregar ícone.
 *
 * O rótulo vem em `neutral-500` no arquivo, que rende 3,12:1 sobre a aba ativa e reprova WCAG AA.
 * Aqui usa `neutral-700` (5,65:1).
 */
const TAB_CLASSES = [
  'inline-flex cursor-pointer appearance-none items-center gap-2 rounded-full border border-solid border-transparent px-6 py-4',
  'bg-transparent font-sans text-sm font-semibold leading-normal text-neutral-700 transition-colors',
  'hover:bg-secondary-100/60 data-[state=active]:border-neutral-300 data-[state=active]:bg-secondary-100',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2'
].join(' ')

/** Os painéis não têm mais moldura própria: cada bloco interno traz a sua. */
const PANEL_CLASSES = 'flex flex-col gap-6 focus-visible:outline-none data-[state=inactive]:hidden'

/** Caixa que envolve o título e a grade de categorias/condições — `neutral-75` sobre `neutral-50`. */
const GRID_BOX_CLASSES = 'flex flex-col rounded-[16px] bg-neutral-75 p-4'

/**
 * Campo de busca ajustado ao design (node 4333:727): fundo claro — o átomo `Form.Input
 * variant="search"` traz `bg-secondary-100`, que sumiria contra a caixa —, raio grande e 44px de
 * altura, medida que vem do botão de lupa (12px de ícone + 8px de padding).
 *
 * **O `Form.Input` aplica o `className` em dois elementos aninhados** — no `container` e também no
 * `containerInput`, que é o filho que carrega a borda. Por isso a moldura vai em `[&>div]:`, que a
 * partir do container externo alcança só o interno: escrita direta, ela pintava fundo, raio e borda
 * duas vezes, e a caixa com borda transbordava o `py` do container externo — a borda aparecia maior
 * que a própria barra. No elemento interno o seletor não casa com nada (os filhos dele são `input` e
 * `svg`), então a duplicação fica inofensiva.
 *
 * `[&_svg]` é por descendência e idempotente: transforma a lupa no botão redondo verde escuro do
 * arquivo, com o glifo em `neutral-50` (6,58:1).
 */
const SEARCH_CLASSES = [
  'h-[44px] [&>div]:h-[44px] [&>div]:rounded-[84px] [&>div]:border-neutral-100 [&>div]:!bg-neutral-50 [&>div]:px-4 [&>div]:py-0',
  '[&_svg]:box-content [&_svg]:size-3 [&_svg]:shrink-0 [&_svg]:rounded-full [&_svg]:bg-marketplace-800 [&_svg]:p-2 [&_svg]:text-neutral-50'
].join(' ')

/**
 * Seção de descoberta da home (Figma node 4333:620).
 *
 * Uma revisão simplificou o cabeçalho — saíram o eyebrow "Dois jeitos de começar", o H2 "Por onde
 * você quer começar?" e o parágrafo de apoio, ficando só "Já sabe o que precisa?". A seguinte
 * trocou as pills das abas por caixas de largura total com ícone. A mais recente voltou atrás
 * nisso: as abas viraram formato de pasta, coladas a um painel que envolve busca e categorias, e
 * "Ou explore por categoria" encurtou para "Ou explore por"; a de 2026-07-31 devolveu
 * "Explore por categoria" (sem o "Ou") na primeira aba.
 *
 * A segunda aba chegou a ter conteúdo próprio no arquivo (oito cards de condição, node 4544:14215),
 * mas a revisão de 2026-07-31 apagou esse nó sem pôr nada no lugar e ainda assim renomeou a aba
 * para "Encontre seu médico". Os cards de condição ficam — ver `constants.ts`.
 *
 * A mesma revisão tirou a moldura única do painel: a busca vive numa caixa `secondary-100` e a
 * grade numa caixa `neutral-75`, e sobrou só um CTA na busca ("Já tenho uma receita" saiu).
 *
 * `data-[state=inactive]:hidden` nos painéis: o Radix mantém o painel inativo no DOM com altura
 * zero, e o `gap` do Root ainda reservava 24px antes dele — a seção saía 26px mais alta.
 *
 * Contraste: o design usa `neutral-500` no placeholder e no rótulo das abas (3,1–3,5:1, reprova
 * WCAG AA). Aqui a busca usa `neutral-700` (via o átomo, já corrigido) e as abas também.
 */
export const DiscoverySection = () => {
  // O id de "Alopático" e "Higiene e Beleza" é gerado pelo banco de cada ambiente (17 e 16 em
  // desenvolvimento, 10 e 9 em produção), então resolvemos pelo nome — ver useProductClassId.
  const { getClassIdByName } = useProductClassId()
  const history = useHistory()
  const { search } = useLocation()
  const [intentSlug, setIntentSlug] = useState<string | null>(null)
  const { data: publicModules } = usePublicScreeningModules()
  const extraModules = (publicModules || []).filter(isNewHomeScreening)
  const categoryCards = buildCategoryCards({
    allopathicId: getClassIdByName('Alopático'),
    hygieneBeautyId: getClassIdByName('Higiene e Beleza')
  })

  return (
    <>
      <section aria-labelledby="discovery-heading" className="w-full bg-neutral-50">
        <div className="mx-auto flex w-full max-w-[1230px] flex-col gap-6 px-8 py-16">
          <Typography.Heading
            id="discovery-heading"
            level={2}
            weight="regular"
            className="m-0 text-center text-[28px] leading-[49.28px] tracking-[-0.448px] text-neutral-800">
            Já sabe o que precisa?
          </Typography.Heading>

          <TabsPrimitive.Root defaultValue="products" className="flex w-full flex-col gap-8">
            <TabsPrimitive.List
              className="flex flex-wrap items-center justify-center gap-2"
              aria-label="Como você quer começar">
              {DISCOVERY_TABS.map((tab) => (
                <TabsPrimitive.Trigger key={tab.value} value={tab.value} className={TAB_CLASSES}>
                  <img src={tab.icon} alt="" aria-hidden className="size-6 shrink-0" />
                  {tab.label}
                </TabsPrimitive.Trigger>
              ))}
            </TabsPrimitive.List>

            <TabsPrimitive.Content value="products" className={PANEL_CLASSES}>
              <div className="flex flex-wrap items-center gap-2 rounded-[16px] bg-secondary-100 p-4">
                <SearchAutocomplete
                  className="min-w-[240px] flex-1"
                  placeholder="Busque por produto, marca ou princípio ativo"
                  inputClassName={SEARCH_CLASSES}
                />
                <CtaPill
                  variant="solidDeep"
                  title="Ver todos os produtos"
                  path="/produtos"
                  className="h-[44px] rounded-[16px]"
                />
              </div>

              <div className={GRID_BOX_CLASSES}>
                <h3 className="m-0 text-base font-semibold leading-[1.4] tracking-[-0.448px] text-neutral-800">
                  Explore por categoria
                </h3>

                <ul className="m-0 grid list-none auto-rows-fr grid-cols-1 gap-2 p-0 sm:grid-cols-2 lg:grid-cols-4">
                  {categoryCards.map((card) => (
                    <li key={card.title}>
                      <Link
                        to={card.path}
                        className="flex h-full flex-col gap-2 rounded-[16px] p-6 no-underline transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2"
                        style={{ backgroundColor: card.background }}>
                        <span
                          aria-hidden
                          className="relative block h-16 shrink-0 overflow-hidden"
                          style={{ width: card.boxWidth }}>
                          <img
                            src={card.image}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="absolute max-w-none"
                            style={{
                              width: card.imageSize,
                              height: card.imageSize,
                              left: card.offsetX,
                              top: card.offsetY
                            }}
                          />
                        </span>

                        <span className="flex items-end justify-between gap-2">
                          <span className="flex min-w-0 flex-col">
                            <span className="text-sm font-semibold leading-normal text-neutral-800">{card.title}</span>
                            <span className="text-xs font-light leading-[17.86px] text-neutral-800">
                              {card.description}
                            </span>
                          </span>

                          <LuArrowRightCircle aria-hidden className="size-4 shrink-0 text-neutral-800" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsPrimitive.Content>

            <TabsPrimitive.Content value="conditions" className={PANEL_CLASSES}>
              <div className={GRID_BOX_CLASSES}>
                <h3 className="m-0 text-base font-semibold leading-[1.4] tracking-[-0.448px] text-neutral-800">
                  Explore por condição
                </h3>

                <ul className="m-0 grid list-none auto-rows-fr grid-cols-1 gap-2 p-0 sm:grid-cols-2 lg:grid-cols-4">
                  {HEALTH_CONDITION_CARDS.map((card) => (
                    <li key={card.title}>
                      <button
                        type="button"
                        onClick={() => {
                          if (card.title === 'Perda de peso') {
                            history.push({ pathname: PAGE_PATH, search: programHomeSearch(search) })
                            return
                          }
                          setIntentSlug(HERO_SLUG_BY_LABEL[card.title] || null)
                        }}
                        className="flex h-full w-full flex-col gap-2 rounded-[16px] p-6 text-left no-underline transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2"
                        style={{ backgroundColor: card.background }}>
                        <img
                          src={card.image}
                          alt=""
                          aria-hidden
                          loading="lazy"
                          decoding="async"
                          className="h-16 shrink-0 object-cover"
                          style={{ width: card.boxWidth }}
                        />

                        <span className="flex items-end justify-between gap-2">
                          <span className="flex min-w-0 flex-col">
                            <span className="text-sm font-semibold leading-normal text-neutral-800">{card.title}</span>
                            <span className="text-xs font-light leading-[17.86px] text-neutral-800">
                              {card.description}
                            </span>
                          </span>

                          <LuArrowRightCircle aria-hidden className="size-4 shrink-0 text-neutral-800" />
                        </span>
                      </button>
                    </li>
                  ))}
                  {extraModules.map((module) => (
                    <li key={module.slug}>
                      <button
                        type="button"
                        onClick={() => setIntentSlug(module.slug)}
                        className="flex h-full w-full flex-col gap-2 rounded-[16px] bg-[#E7E4DC] p-6 text-left no-underline transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2">
                        <span
                          aria-hidden
                          className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/70 text-lg font-semibold text-neutral-800">
                          {module.name.slice(0, 1)}
                        </span>
                        <span className="flex items-end justify-between gap-2">
                          <span className="flex min-w-0 flex-col">
                            <span className="text-sm font-semibold leading-normal text-neutral-800">{module.name}</span>
                            <span className="text-xs font-light leading-[17.86px] text-neutral-800">Triagem</span>
                          </span>
                          <LuArrowRightCircle aria-hidden className="size-4 shrink-0 text-neutral-800" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsPrimitive.Content>
          </TabsPrimitive.Root>
        </div>
      </section>
      <CarePathModal open={!!intentSlug} initialHero={intentSlug} onClose={() => setIntentSlug(null)} />
    </>
  )
}
